import { Injectable } from '@angular/core';

import { bezierProgressToTime } from '../utils/utils';

@Injectable({
    providedIn: 'root',
})
export class WheelAudioService {
    private audioCtx: AudioContext | null = null;

    private tickTimeouts: number[] = [];

    public destroy(): void {
        this.tickTimeouts.forEach(id => clearTimeout(id));
        this.tickTimeouts = [];
        this.audioCtx?.close();
        this.audioCtx = null;
    }

    public playCheer(): void {
        if (!this.audioCtx) return;
        this.playBellStinger();
        this.playFanfare();
    }

    /**
     * Must be called inside a user-gesture handler (e.g. button click)
     * so the browser allows AudioContext creation.
     */
    public scheduleTicks(totalDegrees: number, segments: number, durationMs: number): void {
        const segmentAngle = 360 / segments;

        this.audioCtx ??= new AudioContext();

        for (let deg = segmentAngle; deg < totalDegrees; deg += segmentAngle) {
            const timeFraction = bezierProgressToTime(deg / totalDegrees);
            const id = window.setTimeout(() => this.playTick(), timeFraction * durationMs);
            this.tickTimeouts.push(id);
        }
    }

    private playBell(freq: number, t: number, tail: number): void {
        const ctx = this.audioCtx!;
        // Inharmonic partials (Risset-style ratios) give the metallic bell timbre.
        const partials = [
            { mult: 1.0, amp: 0.32, decay: tail },
            { mult: 2.76, amp: 0.18, decay: tail * 0.7 },
            { mult: 5.4, amp: 0.10, decay: tail * 0.45 },
            { mult: 8.93, amp: 0.06, decay: tail * 0.25 },
        ];
        partials.forEach(p => {
            const osc = ctx.createOscillator();
            osc.type = 'sine';
            osc.frequency.value = freq * p.mult;
            const gain = ctx.createGain();
            gain.gain.setValueAtTime(p.amp, t);
            gain.gain.exponentialRampToValueAtTime(0.001, t + p.decay);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start(t);
            osc.stop(t + p.decay);
        });
    }

    private playBellStinger(): void {
        const ctx = this.audioCtx!;
        // Quick rising bells: G5 → B5 → D6 → G6, last one held for the payoff.
        const notes = [
            { freq: 783.99, delay: 0, tail: 0.45 },
            { freq: 987.77, delay: 0.08, tail: 0.5 },
            { freq: 1174.66, delay: 0.16, tail: 0.6 },
            { freq: 1567.98, delay: 0.24, tail: 1.4 },
        ];
        notes.forEach(n => {
            this.playBell(n.freq, ctx.currentTime + n.delay, n.tail);
        });
    }

    private playFanfare(): void {
        const ctx = this.audioCtx!;
        const notes = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6

        notes.forEach((freq, i) => {
            const isLast = i === notes.length - 1;
            const tail = isLast ? 0.75 : 0.4;
            const t = ctx.currentTime + i * 0.16;
            this.playFanfareNote(freq, t, tail);
        });
    }

    private playFanfareNote(freq: number, t: number, tail: number): void {
        const ctx = this.audioCtx!;
        const detunes = [-7, 0, 7]; // cents — slight detune for fatness

        detunes.forEach(cents => {
            const osc = ctx.createOscillator();
            osc.type = 'sawtooth';
            osc.frequency.value = freq;
            osc.detune.value = cents;

            // Filter sweep gives the "brass attack" formant.
            const filter = ctx.createBiquadFilter();
            filter.type = 'lowpass';
            filter.Q.value = 4;
            filter.frequency.setValueAtTime(700, t);
            filter.frequency.linearRampToValueAtTime(3200, t + 0.06);
            filter.frequency.exponentialRampToValueAtTime(600, t + tail);

            const gain = ctx.createGain();
            const peak = cents === 0 ? 0.18 : 0.08;
            gain.gain.setValueAtTime(0, t);
            gain.gain.linearRampToValueAtTime(peak, t + 0.05);
            gain.gain.linearRampToValueAtTime(peak * 0.7, t + 0.18);
            gain.gain.exponentialRampToValueAtTime(0.001, t + tail);

            osc.connect(filter);
            filter.connect(gain);
            gain.connect(ctx.destination);
            osc.start(t);
            osc.stop(t + tail);
        });
    }

    private playTick(): void {
        if (!this.audioCtx) return;
        const ctx = this.audioCtx;
        const now = ctx.currentTime;
        // Per-click pitch jitter so the cadence doesn't sound mechanical.
        const jitter = 0.92 + Math.random() * 0.16;

        // Pitched body: a short midrange ring — the flapper character that makes
        // it read as a wheel peg rather than a generic blip.
        const body = ctx.createOscillator();
        body.type = 'triangle';
        body.frequency.setValueAtTime(900 * jitter, now);
        body.frequency.exponentialRampToValueAtTime(420 * jitter, now + 0.04);
        const bodyGain = ctx.createGain();
        bodyGain.gain.setValueAtTime(0.22, now);
        bodyGain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
        body.connect(bodyGain);
        bodyGain.connect(ctx.destination);
        body.start(now);
        body.stop(now + 0.09);

        // Bright noise transient: the "smack" of the impact.
        const noiseLen = Math.floor(ctx.sampleRate * 0.012);
        const noiseBuf = ctx.createBuffer(1, noiseLen, ctx.sampleRate);
        const noiseData = noiseBuf.getChannelData(0);
        for (let i = 0; i < noiseLen; i++) {
            noiseData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / noiseLen, 2);
        }
        const noise = ctx.createBufferSource();
        noise.buffer = noiseBuf;
        const noiseFilter = ctx.createBiquadFilter();
        noiseFilter.type = 'bandpass';
        noiseFilter.frequency.value = 2800;
        noiseFilter.Q.value = 2;
        const noiseGain = ctx.createGain();
        noiseGain.gain.value = 0.32;
        noise.connect(noiseFilter);
        noiseFilter.connect(noiseGain);
        noiseGain.connect(ctx.destination);
        noise.start(now);
    }
}
